import { useCallback } from 'react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	useCommentsData,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { useInlineCommentsState } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import type { ResolvedCommentsQueryType } from '@confluence/comments-panel-queries/entry-points/ResolvedCommentsQuery';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { expVal } from '@confluence/feature-experiments';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';

import { getResolvedAnnotationState, updateResolvedComments } from '../helper/commentHelpers';

const RESOLVE = 'resolve';

const useSyncResolvedEditorComments = () => {
	const { unresolvedInlineComments } = useInlineCommentsState();
	const editorAnnotationEventEmitter = getEditorAnnotationEventEmitter();

	return useCallback(() => {
		unresolvedInlineComments.forEach((comment) =>
			editorAnnotationEventEmitter.emit(RESOLVE, comment),
		);
	}, [editorAnnotationEventEmitter, unresolvedInlineComments]);
};

const useSyncResolvedRendererComments = () => {
	const { unresolvedInlineComments } = useInlineCommentsState();
	const rendererAnnotationEventEmitter = getRendererAnnotationEventEmitter();

	return useCallback(() => {
		const resolvedAnnotationState = getResolvedAnnotationState(unresolvedInlineComments);
		rendererAnnotationEventEmitter.emit(
			AnnotationUpdateEvent.SET_ANNOTATION_STATE,
			resolvedAnnotationState,
		);
	}, [rendererAnnotationEventEmitter, unresolvedInlineComments]);
};

export const useSyncResolvedComments = (pageId?: string) => {
	const pageMode = useGetPageMode();
	const [{ commentsDataMap: legacyCommentsDataMap }, { setCommentsDataMap }] = useCommentsData();
	const syncResolvedEditorComments = useSyncResolvedEditorComments();
	const syncResolvedRendererComments = useSyncResolvedRendererComments();
	const { contentStatus } = useContentStatus();

	// New comments data manager hook - only used when experiment is enabled
	const newCommentsDataResult = useAllCommentsData({
		contentId: pageId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(contentStatus),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !pageId,
		},
	});

	// Use the appropriate data source based on experiment
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	return useCallback(
		(resolvedComments: ResolvedCommentsQueryType) => {
			setCommentsDataMap(updateResolvedComments(resolvedComments, commentsDataMap));

			if (pageMode === PageMode.VIEW) {
				syncResolvedRendererComments();
			} else {
				syncResolvedEditorComments();
			}
		},
		[
			commentsDataMap,
			setCommentsDataMap,
			pageMode,
			syncResolvedRendererComments,
			syncResolvedEditorComments,
		],
	);
};
